import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatusPill } from '../../components/StatusPills';
import { CheckCircle2, X, Users, GitMerge } from 'lucide-react';
import { toast } from 'sonner';

const AdminCandidates = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.candidates();
      setItems(res.data?.results || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const verify = async (id) => {
    try {
      await adminApi.verifyOrg(id);
      toast.success('Verified');
      load();
    } catch {
      toast.error('Failed to verify');
    }
  };
  const reject = async (id) => {
    try {
      await adminApi.rejectOrg(id);
      toast.success('Rejected');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reject');
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-candidates-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-foreground">Candidates</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Unverified organisations users have created. Highest-impact first.
        </p>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!loading && items.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            No candidates are waiting for review.
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3">
        {items.map((c) => (
          <Card key={c.id} data-testid={`candidate-${c.id}`}>
            <CardContent className="p-4 sm:p-5 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-semibold text-foreground">{c.name}</h3>
                    <StatusPill tone="needs" size="sm">Unverified</StatusPill>
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {c.type} · {c.privacy_email || 'no privacy email on file'}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span>{c.linked_user_count ?? 0} linked</span>
                </div>
              </div>

              {c.possible_duplicates?.length > 0 && (
                <div className="rounded-lg bg-ground p-3 text-sm">
                  <p className="font-medium text-ink mb-1.5">
                    Possible duplicates. Merge into one of these to recover linked users:
                  </p>
                  <ul className="space-y-1.5">
                    {c.possible_duplicates.map((d) => (
                      <li
                        key={d.id}
                        className="flex items-center justify-between gap-2 flex-wrap"
                        data-testid={`candidate-dup-${c.id}-${d.id}`}
                      >
                        <span className="text-ink-70">
                          ·{' '}
                          <Link to={`/admin/organisations/${d.id}`} className="underline">
                            {d.name}
                          </Link>
                          {d.privacy_email && (
                            <span className="text-ink-55"> · {d.privacy_email}</span>
                          )}
                        </span>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="h-7 text-xs"
                          onClick={() =>
                            navigate(`/admin/merge?source=${c.id}&target=${d.id}`)
                          }
                          data-testid={`candidate-merge-into-${c.id}-${d.id}`}
                        >
                          <GitMerge className="w-3 h-3 mr-1" /> Merge into this
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={() => verify(c.id)}
                  data-testid={`candidate-verify-${c.id}`}
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Verify
                </Button>
                <Link to={`/admin/organisations/${c.id}`}>
                  <Button size="sm" variant="secondary">
                    Edit & verify
                  </Button>
                </Link>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => reject(c.id)}
                  disabled={(c.linked_user_count || 0) > 0}
                  title={
                    (c.linked_user_count || 0) > 0
                      ? 'Has linked user records — merge instead'
                      : 'Soft-delete this candidate'
                  }
                  data-testid={`candidate-reject-${c.id}`}
                >
                  <X className="w-4 h-4 mr-1.5" /> Reject
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminCandidates;
